/**
 * 렌더 중 터진 컴포넌트를 받아 **하얀 화면 대신 안내**를 보여 준다 (사용자 결정 2026-09-11).
 *
 * 지금까지 이 앱에는 에러 경계가 없었다. 컴포넌트 하나가 렌더 중 예외를 던지면 React 는
 * **화면 전체를 비운다** — 사용자는 무엇이 났는지도 모르고 새로고침 말고 할 것이 없으며,
 * 신고는 "그냥 하얘져요" 로 온다. 그리고 **가장 심각한 오류인데 기록도 안 됐다.**
 *
 * ## 두 가지를 한다
 *
 *   1. `logClientError('render', ...)` 로 기록한다 — 기본 수준에서는 스택 **위치**만 남는다
 *      (메시지 본문은 `opsLog/redact` 가 거른다)
 *   2. 안내와 **새로고침 버튼**을 보여 준다
 *
 * ## 클래스 컴포넌트인 이유
 *
 * `componentDidCatch` / `getDerivedStateFromError` 는 **클래스에만** 있다(React 가 그렇게 정했다).
 * 그래서 훅을 못 쓰고, 문구는 `i18n.t()` 를 직접 부른다 — 이 파일이 예외인 이유가 그것이다.
 */
import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../i18n';
import { logClientError } from '../services/opsLog';
import { openFeedback } from './FeedbackDialog';

import { IS_STANDALONE } from '../config/features';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, incidentId: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // **경계가 스스로 깨지면 안 된다.** React 는 componentDidCatch 안에서 던진 예외를 다시
        // 던지고, 그러면 안내 화면도 못 뜬 채 하얀 화면으로 돌아간다 — 고치려던 바로 그 상태다.
        // logClientError 는 원래 던지지 않지만, 그 계약에 안내 화면을 걸지 않는다.
        let incidentId = null;
        try {
            incidentId = logClientError('render', error, {
                // 어느 컴포넌트에서 났는지. 값이 아니라 위치다. `stack` 에 덮어쓰면 JS 스택(어느 줄)이 사라진다.
                componentStack: info && info.componentStack ? String(info.componentStack) : undefined,
            });
        } catch (_e) { /* 기록 실패가 안내를 막지 않는다 */ }
        // **오류 번호를 보여 준다** — 사용자가 번호만 알려 줘도 기록을 찾는다(신고 창에도 골라져 있다)
        if (incidentId) this.setState({ incidentId });
    }

    render() {
        const { error, incidentId } = this.state;
        const { children } = this.props;
        if (!error) return children;

        // **오류 메시지를 화면에 그대로 쓰지 않는다.** 거기에 테이블·컬럼 이름이나 값이 섞여
        // 있을 수 있고, 이 화면은 누가 볼지 모른다. 무엇이 났는지는 로그에서 본다.
        return (
            <div className="eb-wrap" role="alert">
                <div className="eb-box">
                    <div className="eb-title">{i18n.t('common:errorBoundary.title')}</div>
                    <div className="eb-body">{i18n.t('common:errorBoundary.body')}</div>
                    {incidentId ? <div className="eb-incident">{i18n.t('common:errorBoundary.incident', { id: incidentId })}</div> : null}
                    <div className="eb-actions">
                        <button type="button" className="eb-btn" onClick={() => window.location.reload()}>
                            {i18n.t('common:errorBoundary.reload')}
                        </button>
                        <button type="button" className="eb-btn" onClick={() => this.setState({ error: null, incidentId: null })}>
                            {i18n.t('common:errorBoundary.retry')}
                        </button>
                        {IS_STANDALONE ? null : (
                            <button type="button" className="eb-btn" onClick={() => openFeedback({ incidentId })}>
                                {i18n.t('common:errorBoundary.report')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

ErrorBoundary.propTypes = { children: PropTypes.node };
ErrorBoundary.defaultProps = { children: null };

export default ErrorBoundary;
